import type { EntityEventMeta, EntityRecord } from './entityTypes';
import { isLastFmPlaceholderUrl, normalizeKindFromArtistLike, pickFirstNonEmpty, shouldReplaceField } from './mergeUtils';

type AnyObj = Record<string, any>;

const ENTITY_DEBUG =
  typeof window !== 'undefined' &&
  (() => {
    try {
      return window.localStorage.getItem('maestro_entity_debug') === '1';
    } catch {
      return false;
    }
  })();

function debugFieldLog(fieldPath: string, prevVal: unknown, nextVal: unknown, meta: EntityEventMeta) {
  if (!ENTITY_DEBUG) return;
  const watched = new Set(['name', 'imageUrl', 'imageUrlHero', 'biographyNarrative', 'bioSummary']);
  if (!watched.has(fieldPath)) return;
  try {
    console.log('[EntityMerge]', meta.source, fieldPath, { prev: prevVal, next: nextVal, ts: meta.ts, requestId: meta.requestId });
  } catch {
    // ignore
  }
}

function isPlainObject(v: unknown): v is Record<string, any> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function mergeMonotonicObject(
  prevData: AnyObj,
  nextData: AnyObj,
  prevFields: EntityRecord['fields'],
  meta: EntityEventMeta,
  prefix: string
): { data: AnyObj; fields: EntityRecord['fields'] } {
  const out: AnyObj = { ...prevData };
  const fields: EntityRecord['fields'] = { ...prevFields };

  for (const key of Object.keys(nextData || {})) {
    const fieldPath = prefix ? `${prefix}.${key}` : key;
    const nextVal = nextData[key];
    const prevVal = prevData ? prevData[key] : undefined;
    const prevMeta = fields[fieldPath];

    // Recurse for objects to avoid wiping nested fields.
    if (isPlainObject(prevVal) && isPlainObject(nextVal)) {
      const merged = mergeMonotonicObject(prevVal, nextVal, fields, meta, fieldPath);
      out[key] = merged.data;
      Object.assign(fields, merged.fields);
      continue;
    }

    if (shouldReplaceField(prevVal, nextVal, prevMeta, meta.source)) {
      out[key] = nextVal;
      fields[fieldPath] = { source: meta.source, updatedAt: meta.ts, requestId: meta.requestId };
      debugFieldLog(fieldPath, prevVal, nextVal, meta);
    }
  }

  return { data: out, fields };
}

function normalizeArtistPayload(payload: AnyObj): AnyObj {
  const heroRaw = pickFirstNonEmpty(
    payload?.imageUrlHero,
    payload?.image_url_hero,
    payload?.imageUrl,
    payload?.image_url
  );
  const imgRaw = pickFirstNonEmpty(payload?.imageUrl, payload?.image_url, heroRaw);
  const out: AnyObj = { ...payload };

  // Never allow the Last.fm silhouette to enter the merged entity state.
  // We *do not* set empty/null here because the merge layer is monotonic; instead we delete keys.
  if (isLastFmPlaceholderUrl(heroRaw)) {
    delete out.imageUrlHero;
    delete out.image_url_hero;
  } else if (heroRaw) {
    out.imageUrlHero = heroRaw;
  }

  if (isLastFmPlaceholderUrl(imgRaw)) {
    delete out.imageUrl;
    delete out.image_url;
  } else if (imgRaw) {
    out.imageUrl = imgRaw;
  }

  // Normalize biography fields across payload sources (BATON preload vs REST API vs legacy).
  const rawNarr = pickFirstNonEmpty(
    payload?.biographyNarrative,
    payload?.biography_narrative,
    payload?.biography,
    payload?.bio
  );
  const rawSummary = pickFirstNonEmpty(payload?.bioSummary, payload?.bio_summary, payload?.summary);

  // Heuristic: long or templated text should be treated as narrative; short text as summary.
  if (typeof rawNarr === 'string' && rawNarr.trim()) {
    const t = rawNarr.trim();
    const looksNarrative = t.length >= 800 || t.includes('## ') || t.includes('---');
    // IMPORTANT:
    // Do NOT gate assignment on existing out.* fields here.
    // The merge layer (`shouldReplaceField`) already applies monotonic + source-precedence rules,
    // and needs to *see* the incoming value to upgrade stale/unformatted bios.
    if (looksNarrative) out.biographyNarrative = t;
    else out.bioSummary = t;
  }
  if (typeof rawSummary === 'string' && rawSummary.trim()) {
    out.bioSummary = rawSummary.trim();
  }

  // Pass through server intent flags (used to override “never regress to empty” rules).
  if (payload?.imageCleared === true) out.imageCleared = true;
  if (typeof payload?.portraitStatus === 'string') out.portraitStatus = payload.portraitStatus;
  if (typeof payload?.portraitReason === 'string') out.portraitReason = payload.portraitReason;

  return out;
}

function normalizePersonPayload(payload: AnyObj): AnyObj {
  const imgRaw = pickFirstNonEmpty(payload?.imageUrl, payload?.image_url);
  const img = isLastFmPlaceholderUrl(imgRaw) ? null : imgRaw;
  const out: AnyObj = { ...payload };
  if (img) out.imageUrl = img;

  // Normalize biography fields for person payloads too (some sources use biography/bio).
  const rawNarr = pickFirstNonEmpty(
    payload?.biographyNarrative,
    payload?.biography_narrative,
    payload?.biography,
    payload?.bio
  );
  const rawSummary = pickFirstNonEmpty(payload?.bioSummary, payload?.bio_summary, payload?.summary);
  if (typeof rawNarr === 'string' && rawNarr.trim()) {
    const t = rawNarr.trim();
    const looksNarrative = t.length >= 800 || t.includes('## ') || t.includes('---');
    if (looksNarrative) out.biographyNarrative = t;
    else out.bioSummary = t;
  }
  if (typeof rawSummary === 'string' && rawSummary.trim()) {
    out.bioSummary = rawSummary.trim();
  }

  return out;
}

function normalizeAlbumPayload(payload: AnyObj): AnyObj {
  const cover = pickFirstNonEmpty(payload?.coverUrl, payload?.cover_url, payload?.cover, payload?.cover_art_url);
  const coverLarge = pickFirstNonEmpty(payload?.coverUrlLarge, payload?.cover_url_large, cover);
  const out: AnyObj = { ...payload };
  const upgradeHttps = (u: unknown) => {
    const s = String(u || '').trim();
    if (!s) return null;
    // Avoid mixed-content warnings (Cover Art Archive sometimes returns http URLs).
    return s.replace(/^http:\/\//i, 'https://');
  };
  const coverHttps = upgradeHttps(cover);
  const coverLargeHttps = upgradeHttps(coverLarge);
  if (coverHttps) out.coverUrl = coverHttps;
  if (coverLargeHttps) out.coverUrlLarge = coverLargeHttps;
  return out;
}

export function mergeArtistRecord(
  prev: EntityRecord | null,
  payload: AnyObj,
  meta: EntityEventMeta,
  hintKind?: EntityRecord['kind']
): EntityRecord {
  const prevData = (prev?.data as AnyObj) || {};
  const prevFields = prev?.fields || {};
  const normalizedNext = normalizeArtistPayload(payload || {});

  const merged = mergeMonotonicObject(prevData, normalizedNext, prevFields, meta, '');
  const nextData = merged.data;

  // Global UX rule: never regress to black unless we have a replacement.
  // If the server marks a portrait invalid/cleared but does NOT provide a new valid image,
  // keep the currently displayed image (usually preview) until replacement arrives.
  const serverSaysInvalid =
    (normalizedNext as any)?.imageCleared === true ||
    String((normalizedNext as any)?.portraitStatus || '') === 'invalid';
  const incomingHasReplacement = !!pickFirstNonEmpty(
    (normalizedNext as any)?.imageUrlHero,
    (normalizedNext as any)?.image_url_hero,
    (normalizedNext as any)?.imageUrl,
    (normalizedNext as any)?.image_url
  );
  // IMPORTANT:
  // `imageCleared/portraitStatus=invalid` is an invalidation signal about *previous* images.
  // If the server provided a replacement image in this payload, we should show the replacement,
  // not clear to null. We only use this to stop preserving previous images below.

  // Stable images with precedence:
  // - Preview should appear immediately and never regress to empty
  // - But a higher-precedence (db/enriched) image should be allowed to replace a bad preview.
  const heroVal = pickFirstNonEmpty(nextData.imageUrlHero, null);
  const imgVal = pickFirstNonEmpty(nextData.imageUrl, null);
  const heroMeta = merged.fields['imageUrlHero'];
  const imgMeta = merged.fields['imageUrl'];

  const prio = (s?: string) => {
    const src = (s as any) || 'loading';
    // reuse priorities from mergeUtils by importing would be ideal; keep a tiny local mapping to avoid circulars.
    switch (src) {
      case 'enriched': return 50;
      case 'db': return 40;
      case 'freshness': return 35;
      case 'mb': return 30;
      case 'baton': return 20;
      case 'preview': return 10;
      case 'local': return 5;
      default: return 0;
    }
  };

  const candidates: Array<{ v: string; meta?: any }> = [];
  if (heroVal && !isLastFmPlaceholderUrl(heroVal)) candidates.push({ v: heroVal, meta: heroMeta });
  if (imgVal && !isLastFmPlaceholderUrl(imgVal)) candidates.push({ v: imgVal, meta: imgMeta });
  // Also consider previous values to prevent regressions to empty,
  // unless the server explicitly told us the previous image was invalid.
  const preservePrevImages = !(serverSaysInvalid && incomingHasReplacement);
  if (preservePrevImages) {
    if (prevData.imageUrlHero && !isLastFmPlaceholderUrl(prevData.imageUrlHero))
      candidates.push({ v: prevData.imageUrlHero, meta: prev?.fields?.['imageUrlHero'] });
    if (prevData.imageUrl && !isLastFmPlaceholderUrl(prevData.imageUrl))
      candidates.push({ v: prevData.imageUrl, meta: prev?.fields?.['imageUrl'] });
  }

  const best = candidates
    .filter((c) => !!c.v)
    .sort((a, b) => {
      const ap = prio(a.meta?.source);
      const bp = prio(b.meta?.source);
      if (bp !== ap) return bp - ap;
      return (b.meta?.updatedAt || 0) - (a.meta?.updatedAt || 0);
    })[0];

  const stableHero = best?.v || null;
  const stableImg = pickFirstNonEmpty(
    isLastFmPlaceholderUrl(imgVal) ? null : imgVal,
    stableHero,
    preservePrevImages && !isLastFmPlaceholderUrl(prevData.imageUrl) ? prevData.imageUrl : null
  ) || null;

  if (stableHero !== nextData.imageUrlHero) nextData.imageUrlHero = stableHero;
  if (stableImg !== nextData.imageUrl) nextData.imageUrl = stableImg;

  // UX CRITICAL: Never regress biography fields to empty once we have content.
  // If we displayed a bio to the user and a refresh comes back without one,
  // PRESERVE the existing bio rather than showing a blank page.
  // This prevents the "bio appeared, then vanished while I was reading it" bug.
  const prevBioNarrative = prevData.biographyNarrative;
  const prevBioSummary = prevData.bioSummary || prevData.storyTeaser;
  const nextBioNarrative = nextData.biographyNarrative;
  const nextBioSummary = nextData.bioSummary || nextData.storyTeaser;

  // Preserve narrative if we had one and incoming is empty
  if (prevBioNarrative && typeof prevBioNarrative === 'string' && prevBioNarrative.trim().length > 0) {
    if (!nextBioNarrative || (typeof nextBioNarrative === 'string' && nextBioNarrative.trim().length === 0)) {
      nextData.biographyNarrative = prevBioNarrative;
      nextData.biography = prevData.biography || prevBioNarrative; // Also preserve legacy field
    }
  }
  // Preserve summary/teaser if we had one and incoming is empty (and no narrative to show)
  if (!nextData.biographyNarrative && prevBioSummary && typeof prevBioSummary === 'string' && prevBioSummary.trim().length > 0) {
    if (!nextBioSummary || (typeof nextBioSummary === 'string' && nextBioSummary.trim().length === 0)) {
      nextData.bioSummary = prevData.bioSummary;
      nextData.storyTeaser = prevData.storyTeaser;
    }
  }

  if (prevData.profileEntryContext && !nextData.profileEntryContext) {
    nextData.profileEntryContext = prevData.profileEntryContext;
  }

  const kind = normalizeKindFromArtistLike(nextData, hintKind);

  return {
    key: prev?.key || String(nextData?.id || nextData?.external_id || nextData?.name || 'unknown'),
    entityType: 'artist',
    id: nextData?.id || prev?.id || null,
    name: nextData?.name || prev?.name || null,
    kind,
    data: nextData,
    fields: merged.fields,
    sourcesSeen: { ...(prev?.sourcesSeen || {}), [meta.source]: meta.ts },
    updatedAt: meta.ts,
    isLoading: prev?.isLoading ?? false,
    isEnriching: prev?.isEnriching ?? false,
    error: prev?.error ?? null,
    activeRequestId: prev?.activeRequestId ?? meta.requestId,
  };
}

export function mergePersonRecord(prev: EntityRecord | null, payload: AnyObj, meta: EntityEventMeta): EntityRecord {
  const prevData = (prev?.data as AnyObj) || {};
  const prevFields = prev?.fields || {};
  const normalizedNext = normalizePersonPayload(payload || {});
  const merged = mergeMonotonicObject(prevData, normalizedNext, prevFields, meta, '');
  const nextData = merged.data;

  const stableImg = pickFirstNonEmpty(
    isLastFmPlaceholderUrl(nextData.imageUrl) ? null : nextData.imageUrl,
    isLastFmPlaceholderUrl(prevData.imageUrl) ? null : prevData.imageUrl
  );
  if (stableImg && stableImg !== nextData.imageUrl) {
    nextData.imageUrl = stableImg;
    merged.fields['imageUrl'] = merged.fields['imageUrl'] || { source: meta.source, updatedAt: meta.ts, requestId: meta.requestId };
  }

  // UX CRITICAL: Never regress biography fields to empty once we have content.
  // Same protection as artists - preserve bios shown to user.
  const prevBioNarrative = prevData.biographyNarrative;
  const prevBioSummary = prevData.bioSummary || prevData.storyTeaser;
  const nextBioNarrative = nextData.biographyNarrative;
  const nextBioSummary = nextData.bioSummary || nextData.storyTeaser;

  if (prevBioNarrative && typeof prevBioNarrative === 'string' && prevBioNarrative.trim().length > 0) {
    if (!nextBioNarrative || (typeof nextBioNarrative === 'string' && nextBioNarrative.trim().length === 0)) {
      nextData.biographyNarrative = prevBioNarrative;
      nextData.biography = prevData.biography || prevBioNarrative;
    }
  }
  if (!nextData.biographyNarrative && prevBioSummary && typeof prevBioSummary === 'string' && prevBioSummary.trim().length > 0) {
    if (!nextBioSummary || (typeof nextBioSummary === 'string' && nextBioSummary.trim().length === 0)) {
      nextData.bioSummary = prevData.bioSummary;
      nextData.storyTeaser = prevData.storyTeaser;
    }
  }

  const kind = normalizeKindFromArtistLike(nextData);

  return {
    key: prev?.key || String(nextData?.id || nextData?.external_id || nextData?.name || 'unknown'),
    entityType: 'person',
    id: nextData?.id || prev?.id || null,
    name: nextData?.name || prev?.name || null,
    kind: kind === 'group' ? 'person' : kind, // people view should never show group label
    data: nextData,
    fields: merged.fields,
    sourcesSeen: { ...(prev?.sourcesSeen || {}), [meta.source]: meta.ts },
    updatedAt: meta.ts,
    isLoading: prev?.isLoading ?? false,
    isEnriching: prev?.isEnriching ?? false,
    error: prev?.error ?? null,
    activeRequestId: prev?.activeRequestId ?? meta.requestId,
  };
}

export function mergeAlbumRecord(prev: EntityRecord | null, payload: AnyObj, meta: EntityEventMeta): EntityRecord {
  const prevData = (prev?.data as AnyObj) || {};
  const prevFields = prev?.fields || {};
  const normalizedNext = normalizeAlbumPayload(payload || {});
  const merged = mergeMonotonicObject(prevData, normalizedNext, prevFields, meta, '');
  const nextData = merged.data;

  return {
    key: prev?.key || String(nextData?.id || nextData?.external_id || nextData?.title || 'unknown'),
    entityType: 'album',
    id: nextData?.id || prev?.id || null,
    name: nextData?.title || prev?.name || null,
    kind: 'unknown',
    data: nextData,
    fields: merged.fields,
    sourcesSeen: { ...(prev?.sourcesSeen || {}), [meta.source]: meta.ts },
    updatedAt: meta.ts,
    isLoading: prev?.isLoading ?? false,
    isEnriching: prev?.isEnriching ?? false,
    error: prev?.error ?? null,
    activeRequestId: prev?.activeRequestId ?? meta.requestId,
  };
}

